import React from 'react';
import {
  AddressElement,
  CardElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button, Spinner } from 'react-bootstrap';
import { StripeAddressElementOptions, StripeCardElementOptions } from '@stripe/stripe-js';
import { PaymentMethodRequest, SetupIntentPaymentMethodRequest } from '../../../../structures/requests/paymentMethod';

const addressOptions: StripeAddressElementOptions = {
  mode: 'billing',
  allowedCountries: ['US'],
};

const cardOptions: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
    },
  },
};

interface Props {
  clientSecret: string,
  isProcessing: boolean,
  onConfirmCardSetup : (
    data: SetupIntentPaymentMethodRequest | PaymentMethodRequest,
  ) => Promise<void>,
  onCancel: () => Promise<void>,
  setErrors: React.Dispatch<React.SetStateAction<string[]>>,
}

const StripeSetupCardForm: React.FC<Props> = ({
  clientSecret,
  isProcessing,
  onConfirmCardSetup,
  onCancel,
  setErrors,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements || isProcessing) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    const cardElement = elements.getElement('card');
    const addressElement = elements.getElement('address');

    if (!cardElement || !addressElement) {
      return;
    }

    const defaultErrorMessage = 'Could not confirm payment method; check your input and try again.';

    const billingData = {
      name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: 'US',
        postal_code: '',
      },
    };
    if (addressElement) {
      const { value } = await addressElement.getValue();
      if (value) {
        billingData.name = value.name;
        // @ts-ignore
        billingData.address = value.address;
      }
    }
    const { setupIntent, error } = await stripe.confirmCardSetup(
      clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingData,
        },
      },
    );
    if (error) {
      setErrors([error.message || defaultErrorMessage]);
      return;
    }
    if (setupIntent) {
      const { payment_method } = setupIntent;
      if (!payment_method) {
        setErrors([defaultErrorMessage]);
        return;
      }
      const paymentMethodId = typeof payment_method === 'string'
        ? payment_method
        : payment_method.id;

      // Send payment method to server to attach to user
      await onConfirmCardSetup({
        payment_method: paymentMethodId,
      });
    }
  };

  return (
    <div className="bg-white p-4">
      <form onSubmit={handleSubmit}>
        <span className="stripe-card-element-label">Card details</span>
        <div className="stripe-card-element">
          <CardElement
            options={cardOptions}
          />
        </div>
        <AddressElement
          options={addressOptions}
        />
        <div className="mt-4">
          <Button
            type="submit"
            className="mr-4"
            disabled={!stripe || !elements || isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="visually-hidden">Loading...</span>
              </>
            ) : (
              <>Add Card</>
            )}
          </Button>
          <Button
            disabled={isProcessing}
            onClick={async () => {
              await onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StripeSetupCardForm;
